import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {TaillightBitmap, TaillightBitmapPaint} from './taillight-bitmap'
import {TaillightCSS} from './taillight-css-renderer'
import {TaillightFrame} from './taillight-frame'
import {TaillightsGlyphs} from './taillights-glyphs'
import {taillightPlacementProperties, type TaillightProps} from './taillight-types'
import styles from './taillight.module.css'

const placementProperties = new Set<string>(taillightPlacementProperties)

/** Selects a renderer for one prepared block, keeping CSS and bitmap hooks isolated. */
export function Taillight(props: TaillightProps) {
  for (const property of Object.keys(props.style ?? {})) {
    if (!placementProperties.has(property)) {
      throw new Error(
        `Unsupported Taillight style property: ${property}. Use TaillightsProvider rendering for typography and padding.`,
      )
    }
  }
  const hasSpanFallback =
    props.block.paint === 'spans' ||
    (props.block.paint === 'mixed' && props.block.parts.some(part => part.paint === 'spans'))
  const css = props.renderer === 'css' && !hasSpanFallback
  if (!css && props.block.paint === 'bitmap' && props.inert && !props.isSelectable && props.children == null) {
    return <TaillightBitmap {...props} />
  }
  return (
    <TaillightFrame {...props} renderer={css ? 'css' : 'bitmap'} controls={props.children}>
      {css ? (
        <TaillightCSS {...props} />
      ) : props.block.paint === 'mixed' ? (
        <div aria-hidden>
          {props.block.parts.map(part => {
            const cssPart = props.renderer === 'css' && part.paint !== 'spans'
            return (
              <Taillight
                key={part.id}
                block={part}
                renderer={props.renderer}
                inert={cssPart ? props.inert : true}
                isSelectable={cssPart && props.isSelectable}
                hidePaint={props.hidePaint}
                onRenderError={props.onRenderError}
              />
            )
          })}
        </div>
      ) : props.block.paint === 'bitmap' ? (
        <TaillightBitmapPaint {...props} />
      ) : (
        <TaillightSpans {...props} />
      )}
    </TaillightFrame>
  )
}

function TaillightSpans(props: TaillightProps) {
  return (
    <pre
      {...testIdProps('taillights-chunk')}
      translate="no"
      className={clsx(styles.Paint, styles.nonSelectable, props.hidePaint && styles.hiddenPaint)}
      inert
      aria-hidden
    >
      <TaillightsGlyphs text={props.block.text} runs={props.block.glyphRuns} pseudoText palette={props.block.palette} />
    </pre>
  )
}
